
import { useQuery } from "@apollo/client";
import { Alert, Box, Card, CardContent, Typography } from "@mui/material";
import DoughnutChart from "~/components/common/charts/DoughnutChart";
import { GET_PROVIDER_UTILIZATION_CHART } from "../kpiQueries";
import { GetKpiProviderUtilizationQuery } from "~/graphql/__generated__/graphql";
import { useState } from "react";
import { ChartData } from "chart.js";
import SkeletonDoughnutChart from "~/components/common/charts/SkeletonDoughnutChart";
import { VisitTypeColors } from "~/utils/statuses";
import { calculateProviderUtilization } from "@caredna-marlin/utils/kpisHelper";

interface Props {
  startTime?: Date;
  endTime?: Date;
  providerId?: number;
  serviceLineId?: number;
}

export const ProviderUtilizationChart: React.FC<Props> = ({
  startTime,
  endTime,
  serviceLineId,
  providerId,
}) => {
  const [error, setError] = useState('');
  const { data, loading } = useQuery<GetKpiProviderUtilizationQuery>(
    GET_PROVIDER_UTILIZATION_CHART, { 
      variables: {
        input: {
          startTime: startTime?.toISOString(),
          endTime: endTime?.toISOString(),
          serviceLineId,
          providerId,
        }
      },
      onError(error) {
        console.error('ProviderUtilizationChart Error: ->', error);
        setError('Something when wrong when loading the data');
      },
      onCompleted() {
        console.log('Completed', data);
        setError('');
      }
    },  
  );

  const shiftTime = Number(data?.kpiProviderUtilization?.shiftTime ?? 0);
  const visitTime = Number(data?.kpiProviderUtilization?.visitTime ?? 0);
  const interVisitDriveTime = Number(data?.kpiProviderUtilization?.interVisitDriveTime ?? 0);
  const startEndDriveTime = Number(data?.kpiProviderUtilization?.startEndDriveTime ?? 0);
  const calendarBlockTime = Number(data?.kpiProviderUtilization?.calendarBlockTime ?? 0);

  const providerUtilization = shiftTime && visitTime ? calculateProviderUtilization({
    shiftDuration: shiftTime,
    visitResourcesDuration: visitTime,
    breaksDuration: calendarBlockTime
  }) : 0;

  const labels = !serviceLineId ? ["Visit Time", "Intervisit Drive Time", "Before/After Shift Drive Time", "Down Time"] : ["Visit Time", "Intervisit Drive Time", "Before/After Shift Drive Time"];

  const totalDownTime = Math.max(shiftTime - visitTime - interVisitDriveTime - startEndDriveTime, 0);

  const values = !serviceLineId ? [visitTime, interVisitDriveTime, startEndDriveTime, totalDownTime] : [visitTime, interVisitDriveTime, startEndDriveTime];

  const graphData = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: VisitTypeColors,
        hoverBackgroundColor: VisitTypeColors,
      },
    ],
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {loading ? <Box sx={{ maxWidth: '80%' }}><SkeletonDoughnutChart title="Provider Utilization" /></Box> : (
          error ? (
            <Alert severity="error">
              {error}
            </Alert>
          ) : (
            !graphData || !graphData?.datasets?.length || providerUtilization === 0  ? (
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Alert severity="warning" sx={{ width: '100%' }}>
                  No Data
                </Alert>
              </Box>
            ) : (
              <>
                <Typography fontWeight={500}>Provider Utilization</Typography>
                <DoughnutChart
                  data={graphData as ChartData<"doughnut">}
                  centerText={`${providerUtilization}%`}
                  loading={false}
                  legendPosition="bottom"
                />
              </>
            )
          )
        )}
      </CardContent>
    </Card>
  )
}
